
import { Link } from 'react-router-dom';

export default function MotivationTheoryPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-600 via-orange-600 to-yellow-600 text-white py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto">
            <Link to="/motivation" className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors">
              <i className="ri-arrow-left-line mr-2"></i>
              動機付け一覧に戻る
            </Link>
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl mb-6">
              <i className="ri-lightbulb-line text-4xl"></i>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">動機づけ理論</h1>
            <p className="text-xl text-white/90">Motivation Theory：総論</p>
          </div>
        </div>
      </section>

      {/* Core Message */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-2xl shadow-2xl p-8 md:p-12 text-white text-center">
              <h2 className="text-3xl font-bold mb-4">やる気は性格ではなく、環境との相互作用で生まれる</h2>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* 理論の概要 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-amber-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-book-open-line text-xl text-white"></i>
                </div>
                理論の概要
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-gray-700 leading-relaxed mb-4">
                  動機づけ理論は、人間の行動を引き起こし、方向づけ、維持する心理的プロセスを説明する理論の総称です。
                </p>
                <p className="text-gray-700 leading-relaxed mb-4">
                  多くの管理者は「やる気がない職員」を個人の性格や意識の問題として捉えがちですが、動機づけ理論の視点では、<strong className="text-amber-600">やる気は環境との相互作用によって生まれる</strong>ものです。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  つまり、職員のモチベーション低下は、個人の問題ではなく、<strong className="text-amber-600">環境や構造の課題</strong>として捉えるべきなのです。
                </p>
              </div>
            </div>

            {/* 薬局運営への活用 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-amber-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-hospital-line text-xl text-white"></i>
                </div>
                薬局運営への活用
              </h2>
              <div className="space-y-6">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-user-line text-amber-600 mr-2"></i>
                    やる気低下を個人問題にしない
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    「あの人はやる気がない」「意識が低い」という評価をやめ、「どのような環境要因がモチベーションを下げているのか」を分析する視点に切り替えます。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6 border border-orange-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    <i className="ri-settings-3-line text-orange-600 mr-2"></i>
                    構造課題として捉える
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    モチベーション低下の原因を、業務設計・評価制度・コミュニケーション構造などの<strong>構造的な課題</strong>として捉えます。
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-orange-500 mr-2 mt-1"></i>
                      <span>業務の裁量権が少なすぎないか？</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-orange-500 mr-2 mt-1"></i>
                      <span>成長実感を得られる機会があるか？</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-orange-500 mr-2 mt-1"></i>
                      <span>職場での関係性は良好か？</span>
                    </li>
                    <li className="flex items-start">
                      <i className="ri-checkbox-circle-fill text-orange-500 mr-2 mt-1"></i>
                      <span>評価基準は明確で公平か？</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* 実践のポイント */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-amber-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-lightbulb-flash-line text-xl text-white"></i>
                </div>
                実践のポイント
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-eye-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">観察する</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    職員の行動や発言から、モチベーションの変化を早期に察知する。定期的な1on1や職場アンケートを活用する。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-xl p-6 border border-orange-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-question-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">問いかける</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    「なぜやる気が出ないのか」ではなく、「どのような環境があればもっと働きやすいか」を問いかける。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-xl p-6 border border-yellow-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-tools-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">環境を変える</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    個人を変えようとするのではなく、業務プロセス・評価制度・コミュニケーション方法など、環境を変える。
                  </p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6 border border-amber-200">
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-lg flex items-center justify-center mb-4">
                    <i className="ri-line-chart-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-3">測定する</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    施策の効果を定期的に測定し、改善を続ける。エンゲージメントスコアや離職率などの指標を活用する。
                  </p>
                </div>
              </div>
            </div>

            {/* 他の理論との関連 */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-amber-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex items-center justify-center mr-3">
                  <i className="ri-links-line text-xl text-white"></i>
                </div>
                他の理論との関連
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                動機づけ理論は総論であり、以下の個別理論がより具体的な視点を提供します。
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                <Link to="/motivation/self-determination-theory" className="flex items-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl border border-blue-200 hover:shadow-lg transition-all group">
                  <i className="ri-user-star-line text-2xl text-blue-600 mr-3"></i>
                  <div>
                    <div className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors">自己決定理論</div>
                    <div className="text-xs text-gray-600">内発的動機づけの3要素</div>
                  </div>
                </Link>

                <Link to="/motivation/intrinsic-extrinsic" className="flex items-center p-4 bg-gradient-to-br from-rose-50 to-pink-50 rounded-xl border border-rose-200 hover:shadow-lg transition-all group">
                  <i className="ri-heart-line text-2xl text-rose-600 mr-3"></i>
                  <div>
                    <div className="font-bold text-gray-900 group-hover:text-rose-600 transition-colors">内発的／外発的動機づけ</div>
                    <div className="text-xs text-gray-600">報酬と意味の違い</div>
                  </div>
                </Link>

                <Link to="/motivation/goal-setting" className="flex items-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200 hover:shadow-lg transition-all group">
                  <i className="ri-flag-line text-2xl text-green-600 mr-3"></i>
                  <div>
                    <div className="font-bold text-gray-900 group-hover:text-green-600 transition-colors">目標設定理論</div>
                    <div className="text-xs text-gray-600">機能する目標の条件</div>
                  </div>
                </Link>

                <Link to="/motivation/burnout" className="flex items-center p-4 bg-gradient-to-br from-red-50 to-rose-50 rounded-xl border border-red-200 hover:shadow-lg transition-all group">
                  <i className="ri-fire-line text-2xl text-red-600 mr-3"></i>
                  <div>
                    <div className="font-bold text-gray-900 group-hover:text-red-600 transition-colors">バーンアウト理論</div>
                    <div className="text-xs text-gray-600">消耗のメカニズム</div>
                  </div>
                </Link>
              </div>
            </div>

            {/* 明日の一手 */}
            <div className="bg-gradient-to-br from-amber-600 via-orange-600 to-yellow-600 rounded-2xl shadow-2xl p-8 text-white">
              <h2 className="text-2xl font-bold mb-4 flex items-center">
                <i className="ri-calendar-check-line text-3xl mr-3"></i>
                明日の一手
              </h2>
              <p className="text-lg text-white/90 leading-relaxed mb-6">
                まずは、職員のモチベーション低下を「個人の問題」として捉えていないか、自分自身の視点を振り返ってみましょう。
              </p>
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <p className="text-white font-semibold mb-2">具体的なアクション：</p>
                <p className="text-white/90 leading-relaxed">
                  次回の1on1やミーティングで、「どうすればもっと働きやすくなるか」「どのような環境があれば成長実感を得られるか」を職員に問いかけてみる。
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center pt-8 border-t border-gray-200">
              <Link to="/motivation" className="flex items-center text-amber-600 hover:text-amber-700 font-semibold transition-colors">
                <i className="ri-arrow-left-line mr-2"></i>
                動機付け一覧に戻る
              </Link>
              <Link to="/motivation/self-determination-theory" className="flex items-center text-amber-600 hover:text-amber-700 font-semibold transition-colors">
                次の理論へ
                <i className="ri-arrow-right-line ml-2"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
